/**
 * WorkflowExecutionsDialog - Painel de execuções do Workflow 2.0
 * -----------------------------------------------------------------------------
 * Observabilidade para o usuário final: mostra quantas execuções o processo
 * teve, quantas terminaram bem, quantas falharam e o que aconteceu em cada uma.
 *
 * Dois modos:
 *   - por processo (`definitionId` informado): usado no construtor;
 *   - geral (sem `definitionId`): usado nos cards KPI da página de Workflow,
 *     exibindo também a coluna "Processo".
 *
 * Ao clicar em uma execução, abre a linha do tempo dos blocos percorridos,
 * com a mensagem de erro exata de cada ação automática que falhou.
 *
 * @version 1.4.0
 * RELEASE: 2026-10-06 v1.4.0 - Cada passo ganha "Detalhes da fase" (configuração,
 *          ações, caminhos) reaproveitando `get_workflow_instance_timeline` e o
 *          componente `DetalhesDaFase` da ficha do negócio. Entrada/saída crus
 *          viram "Dados técnicos" recolhidos, exibidos só quando têm conteúdo.
 * RELEASE: 2026-10-06 v1.3.0 - Modo página usa o `PaginationFooter` padrão (mesmo
 *          padrão da página Pendências), com tamanho de página configurável
 *          (10/25/50/100). Modo diálogo mantém o rodapé original com "Fechar".
 * RELEASE: 2026-10-06 v1.2.0 - Modo página (`asPage`) para o submenu Execuções e
 *          coluna com atalho "Abrir registro" (`onOpenRecord`). Modo diálogo inalterado.
 * RELEASE: 2026-09-06 v1.1.0 - Detalhamento dos passos no mesmo leiaute da
 *          página de fluxo (círculo numerado, ícone de status, duração,
 *          ID do nó e dados de entrada/saída expansíveis); selos de situação
 *          substituídos por bolinha colorida + texto (padrão
 *          ConnectionStatusDot).
 * RELEASE: 2026-09-06 v1.0.0 - Criação inicial (observabilidade do módulo).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { DetalhesDaFase } from '@/components/workflow/WorkflowProcessPanel';
// v1.6.0: liga passos de versões antigas aos detalhes da fase (BR-WF-EXEC-002)
import { ligarDetalhesAosPassos } from '@/lib/workflow/executionPhaseDetails';
import { WorkflowRetryStepButton } from '@/components/workflow/WorkflowRetryStepButton';
import { podeReexecutarEtapa } from '@/lib/workflow/retryParkedStep';
import { usePermissions } from '@/hooks/usePermissions';
import type {
  WorkflowInstanceProgress,
  WorkflowTrailStep,
} from '@/hooks/useWorkflowInstanceTimeline';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Clock,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { KpiCard } from '@/components/layout/list-page';
// v1.3.0: rodapé de paginação padrão do projeto (mesmo da página Pendências)
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { WorkflowDialogShell } from '@/components/workflow/WorkflowDialogShell';
import {
  useWorkflowExecutionDetail,
  useWorkflowExecutionLog,
  type WorkflowExecutionItem,
  type WorkflowExecutionStep,
} from '@/hooks/useWorkflowExecutions';
import { formatUuidShort } from '@/lib/formatters';
import { cn } from '@/lib/utils';

/** Quantidade de execuções por página */
const TAMANHO_PAGINA = 25;

/**
 * Aparência de cada situação (rótulo, cor da bolinha e ícone).
 * Padrão visual: bolinha colorida + texto em cor normal (sem selo cheio),
 * seguindo o `ConnectionStatusDot` e a linha do tempo da página de fluxo.
 */
const STATUS_META: Record<string, { rotulo: string; bolinha: string; Icone: typeof CheckCircle2; corIcone: string }> = {
  // Situações da execução (instância)
  ativo:       { rotulo: 'Em andamento', bolinha: 'bg-blue-500',            Icone: Play,         corIcone: 'text-blue-500' },
  aguardando:  { rotulo: 'Aguardando',   bolinha: 'bg-amber-500',           Icone: Pause,        corIcone: 'text-amber-500' },
  concluido:   { rotulo: 'Concluído',    bolinha: 'bg-emerald-500',         Icone: CheckCircle2, corIcone: 'text-emerald-500' },
  cancelado:   { rotulo: 'Cancelado',    bolinha: 'bg-muted-foreground/50', Icone: Ban,          corIcone: 'text-muted-foreground' },
  erro:        { rotulo: 'Com erro',     bolinha: 'bg-destructive',         Icone: XCircle,      corIcone: 'text-destructive' },
  compensando: { rotulo: 'Revertendo',   bolinha: 'bg-amber-500',           Icone: Pause,        corIcone: 'text-amber-500' },
  compensado:  { rotulo: 'Revertido',    bolinha: 'bg-muted-foreground/50', Icone: Ban,          corIcone: 'text-muted-foreground' },
  // Situações de cada bloco (passo)
  pendente:    { rotulo: 'Pendente',     bolinha: 'bg-muted-foreground/50', Icone: Clock,        corIcone: 'text-muted-foreground' },
  agendado:    { rotulo: 'Agendado',     bolinha: 'bg-amber-500',           Icone: Clock,        corIcone: 'text-amber-500' },
  executando:  { rotulo: 'Executando',   bolinha: 'bg-blue-500',            Icone: Play,         corIcone: 'text-blue-500' },
  falhou:      { rotulo: 'Falhou',       bolinha: 'bg-destructive',         Icone: XCircle,      corIcone: 'text-destructive' },
};

/** Situação desconhecida: cinza discreto com o código cru como rótulo */
const STATUS_DESCONHECIDO = {
  rotulo: '',
  bolinha: 'bg-muted-foreground/50',
  Icone: AlertTriangle,
  corIcone: 'text-muted-foreground',
};

/** Bolinha colorida + palavra em cor de texto normal (padrão do módulo) */
function StatusDot({ status, className }: { status: string; className?: string }) {
  const meta = STATUS_META[status] ?? { ...STATUS_DESCONHECIDO, rotulo: status };
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs text-foreground', className)}>
      <span className={cn('h-2 w-2 shrink-0 rounded-full', meta.bolinha)} />
      {meta.rotulo}
    </span>
  );
}

/** Data/hora curta no padrão brasileiro */
function dataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Converte segundos em texto curto (ex.: 1h 12m) */
function duracao(segundos: number | null | undefined): string {
  if (segundos === null || segundos === undefined) return '—';
  const s = Math.max(0, Math.floor(Number(segundos)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

interface WorkflowExecutionsDialogProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Quando informado, restringe as execuções a um processo */
  definitionId?: string | null;
  /** Nome do processo (apenas para a descrição do modal) */
  definitionName?: string | null;
  /** Filtro inicial de situação (usado ao clicar nos cards KPI) */
  statusInicial?: string;
  /** Filtro inicial de período (usado pelo card "Hoje") */
  periodoInicial?: string;
  /** v1.2.0: renderiza como página (sem janela modal) */
  asPage?: boolean;
  /** v1.2.0: abre a ficha oficial do registro de origem */
  onOpenRecord?: (type: string, id: string | null) => void;
  /** v1.5.0: exibe "Reexecutar etapa" (somente a página Execuções liga) */
  allowRetryStep?: boolean;
}

export function WorkflowExecutionsDialog({
  open,
  onOpenChange,
  definitionId = null,
  definitionName = null,
  statusInicial = 'todos',
  periodoInicial = 'todos',
  asPage = false,
  onOpenRecord,
  allowRetryStep = false,
}: WorkflowExecutionsDialogProps) {
  const { canEdit } = usePermissions();
  const [status, setStatus] = useState(statusInicial);
  const [periodo, setPeriodo] = useState(periodoInicial);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  // v1.3.0: tamanho da página passou a ser configurável (padrão do PaginationFooter)
  const [tamanho, setTamanho] = useState(TAMANHO_PAGINA);
  const [selecionada, setSelecionada] = useState<string | null>(null);

  // Reaplica os filtros de entrada sempre que o modal é aberto
  useEffect(() => {
    if (!open) return;
    setStatus(statusInicial);
    setPeriodo(periodoInicial);
    setBusca('');
    setPagina(1);
    setSelecionada(null);
  }, [open, statusInicial, periodoInicial]);

  const { kpis, instances, total, isLoading, isFetching, refetch } = useWorkflowExecutionLog({
    definitionId,
    status,
    period: periodo,
    search: busca,
    page: pagina,
    pageSize: tamanho,
    enabled: open || asPage,
  });

  const { detalhe, isLoading: carregandoDetalhe } = useWorkflowExecutionDetail(selecionada);

  // v1.4.0: detalhes de auditoria por fase (mesma fonte da ficha do negócio).
  // Consulta pela entidade e escolhe o processo desta execução; mapeia por node_id.
  const raizTipo = detalhe?.instance?.root_entity_type ?? null;
  const raizId = detalhe?.instance?.root_entity_id ?? null;
  const { data: detalhesPorNo } = useQuery({
    queryKey: ['workflow-execution-phase-details', selecionada, raizTipo, raizId, detalhe?.steps?.length ?? 0],
    enabled: !!selecionada && !!raizTipo && !!raizId && !!detalhe,
    staleTime: 30_000,
    queryFn: async (): Promise<Map<string, WorkflowTrailStep>> => {
      const { data, error } = await (supabase.rpc as any)('get_workflow_instance_timeline', {
        p_instance_id: null,
        p_root_entity_type: raizTipo,
        p_root_entity_id: raizId,
      });
      if (error) {
        logger.error('Erro ao carregar detalhes das fases da execução:', error.message);
        throw error;
      }
      const payload = (data ?? null) as WorkflowInstanceProgress | null;
      const processo =
        (payload?.processos ?? []).find((p) => p.instance_id === selecionada) ??
        (payload?.instance_id === selecionada ? payload : null);
      // v1.6.0 (BR-WF-EXEC-002): os passos usam o bloco da versão em que rodaram e a
      // linha do tempo usa a versão vigente; a ligação é feita pela regra pura.
      const mapa = new Map<string, WorkflowTrailStep>();
      (processo?.etapas ?? []).forEach((e) => mapa.set(e.node_id, e));
      const passos = detalhe?.steps ?? [];
      const ligados = ligarDetalhesAosPassos(
        passos.map((s: WorkflowExecutionStep) => ({ node_id: s.node_id, node_type: s.node_type, node_label: s.node_label })),
        processo?.etapas ?? [],
      );
      passos.forEach((s: WorkflowExecutionStep, i: number) => {
        if (s.node_id && ligados[i] && !mapa.has(s.node_id)) {
          mapa.set(s.node_id, { ...(processo!.etapas.find((e) => e.detalhes === ligados[i]) as WorkflowTrailStep) });
        }
      });
      return mapa;
    },
  });

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(total / tamanho)),
    [total, tamanho],
  );

  /** Aplica um filtro de situação e volta para a primeira página */
  const filtrarPor = (valor: string) => {
    setStatus(valor);
    setPagina(1);
  };

  // v1.3.0: modo página usa o PaginationFooter padrão (padrão de Pendências);
  // modo diálogo mantém o rodapé original com o botão "Fechar"
  const rodape = asPage ? (
    <PaginationFooter
      totalItems={total}
      currentPage={pagina}
      pageSize={tamanho}
      onPageChange={setPagina}
      onPageSizeChange={setTamanho}
      pageSizeOptions={[10, 25, 50, 100]}
      // O container da página já aplica a borda superior (sem duplicar)
      className="mt-0 pt-0 border-t-0"
    />
  ) : (
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {total} execução(ões) · página {pagina} de {totalPaginas}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina <= 1}
              onClick={() => setPagina((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina >= totalPaginas}
              onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
            >
              Próxima
            </Button>
            <Button size="sm" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        </div>
  );

  const conteudo = (
    <>
      {/* ===================================================================
          Detalhe de uma execução (linha do tempo)
          =================================================================== */}
      {selecionada ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelecionada(null)}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Voltar para a lista
            </Button>
            <div className="flex items-center gap-3">
              {/* v1.5.0: reexecução manual só na página Execuções (BR-WF-EXEC-001) */}
              {detalhe?.instance &&
                podeReexecutarEtapa({
                  permitidoNaTela: allowRetryStep,
                  podeEditar: canEdit('workflow_executions'),
                  status: detalhe.instance.status,
                  emRevisao: detalhe.instance.needs_review,
                }) && <WorkflowRetryStepButton instanceId={detalhe.instance.id} />}
              {detalhe?.instance && <StatusDot status={detalhe.instance.status} />}
            </div>
          </div>

          {carregandoDetalhe && (
            <p className="text-sm text-muted-foreground">Carregando detalhamento…</p>
          )}

          {detalhe && (
            <>
              <div className="grid gap-2 rounded-lg border p-3 text-xs sm:grid-cols-2">
                <p>
                  <span className="text-muted-foreground">Processo: </span>
                  {detalhe.instance.definition_name}
                </p>
                {/* v1.7.0: versão executada (e vigente, quando diferente) */}
                <p>
                  <span className="text-muted-foreground">Versão: </span>
                  {detalhe.instance.version_number != null ? `v${detalhe.instance.version_number}` : '—'}
                  {detalhe.instance.current_version_number != null &&
                    detalhe.instance.current_version_number !== detalhe.instance.version_number && (
                      <span className="text-muted-foreground"> (atual: v{detalhe.instance.current_version_number})</span>
                    )}
                </p>
                <p>
                  <span className="text-muted-foreground">Registro: </span>
                  {detalhe.instance.root_entity_id
                    ? formatUuidShort(detalhe.instance.root_entity_id)
                    : '—'}
                </p>
                <p>
                  <span className="text-muted-foreground">Início: </span>
                  {dataHora(detalhe.instance.started_at)}
                </p>
                <p>
                  <span className="text-muted-foreground">Fim: </span>
                  {dataHora(detalhe.instance.completed_at ?? detalhe.instance.cancelled_at)}
                </p>
                {detalhe.instance.error_summary && (
                  <p className="sm:col-span-2 text-destructive">
                    {detalhe.instance.error_summary}
                  </p>
                )}
              </div>

              {/* ===== Passos executados (mesmo leiaute da página de fluxo) ===== */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Passos executados:</h4>
                {detalhe.steps.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Esta execução ainda não registrou blocos executados.
                  </p>
                )}
                {detalhe.steps.map((etapa: WorkflowExecutionStep, indice: number) => {
                  const meta = STATUS_META[etapa.status] ?? { ...STATUS_DESCONHECIDO, rotulo: etapa.status };
                  // Duração do passo em segundos (início → fim), quando disponível
                  const segundos =
                    etapa.started_at && etapa.finished_at
                      ? Math.round(
                          (new Date(etapa.finished_at).getTime() -
                            new Date(etapa.started_at).getTime()) /
                            1000,
                        )
                      : null;
                  return (
                    <div
                      key={etapa.id}
                      className="flex items-start gap-3 rounded-lg border bg-muted/50 p-3"
                    >
                      {/* Círculo numerado com a ordem do passo */}
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                        {indice + 1}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <meta.Icone className={cn('h-4 w-4', meta.corIcone)} />
                          <span className="text-sm font-medium">
                            {etapa.node_label || etapa.node_type || 'Bloco removido'}
                          </span>
                          {segundos !== null && (
                            <span className="text-xs text-muted-foreground">
                              ({duracao(segundos)})
                            </span>
                          )}
                          <StatusDot status={etapa.status} className="ml-auto" />
                          {(etapa.attempt ?? 0) > 1 && (
                            <Badge variant="outline">{etapa.attempt} tentativas</Badge>
                          )}
                        </div>

                        <div className="mb-1 text-xs text-muted-foreground">
                          ID do Nó: {etapa.node_id ? formatUuidShort(etapa.node_id) : '—'}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          Início {dataHora(etapa.started_at ?? etapa.created_at)} · Fim{' '}
                          {dataHora(etapa.finished_at)}
                        </div>

                        {etapa.error_message && (
                          <div className="mt-2 break-words rounded bg-destructive/10 p-2 text-xs text-destructive">
                            {etapa.error_message}
                          </div>
                        )}

                        {/* v1.4.0: detalhes da fase (configuração, ações e caminhos) */}
                        <details className="mt-2" open={indice === 0}>
                          <summary className="cursor-pointer text-xs text-primary hover:underline">
                            Detalhes da fase
                          </summary>
                          <div className="mt-2 rounded bg-background p-3">
                            <DetalhesDaFase
                              nodeType={etapa.node_type ?? ''}
                              detalhes={
                                etapa.node_id ? detalhesPorNo?.get(etapa.node_id)?.detalhes ?? null : null
                              }
                            />
                          </div>
                        </details>

                        {/* v1.4.0: dados técnicos crus (somente quando há conteúdo real) */}
                        {(() => {
                          const temDados = (v: unknown) =>
                            v != null && !(typeof v === 'object' && Object.keys(v as object).length === 0);
                          const entrada = (etapa.input ?? null) as Record<string, unknown> | null;
                          const caminho =
                            entrada && typeof entrada.branch_id === 'string' ? entrada.branch_id : null;
                          // Remove o branch_id do JSON (exibido à parte como "Caminho percorrido")
                          const restoEntrada =
                            entrada && typeof entrada === 'object'
                              ? Object.fromEntries(Object.entries(entrada).filter(([k]) => k !== 'branch_id'))
                              : entrada;
                          const mostrarEntrada = temDados(restoEntrada);
                          const mostrarSaida = temDados(etapa.output);
                          if (!caminho && !mostrarEntrada && !mostrarSaida) return null;
                          return (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-xs text-muted-foreground hover:underline">
                                Dados técnicos
                              </summary>
                              <div className="mt-1 space-y-1 text-xs">
                                {caminho && (
                                  <p>
                                    <span className="text-muted-foreground">Caminho percorrido: </span>
                                    {formatUuidShort(caminho)}
                                  </p>
                                )}
                                {mostrarEntrada && (
                                  <pre className="overflow-x-auto rounded bg-background p-2">
                                    {JSON.stringify(restoEntrada, null, 2)}
                                  </pre>
                                )}
                                {mostrarSaida && (
                                  <pre className="overflow-x-auto rounded bg-background p-2">
                                    {JSON.stringify(etapa.output, null, 2)}
                                  </pre>
                                )}
                              </div>
                            </details>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ) : (
        /* ===================================================================
           Lista de execuções + indicadores
           =================================================================== */
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <KpiCard
              color="purple"
              icon={Activity}
              label="Total"
              value={kpis.total}
              onClick={() => filtrarPor('todos')}
              active={status === 'todos'}
            />
            <KpiCard
              color="blue"
              icon={Clock}
              label="Em andamento"
              value={kpis.em_andamento}
              onClick={() => filtrarPor('em_andamento')}
              active={status === 'em_andamento'}
            />
            <KpiCard
              color="green"
              icon={CheckCircle2}
              label="Concluídas"
              value={kpis.concluidas}
              onClick={() => filtrarPor('concluidas')}
              active={status === 'concluidas'}
            />
            <KpiCard
              color="red"
              icon={XCircle}
              label="Com erro"
              value={kpis.com_erro}
              onClick={() => filtrarPor('com_erro')}
              active={status === 'com_erro'}
            />
            <KpiCard
              color="orange"
              icon={AlertTriangle}
              label="Hoje"
              value={kpis.hoje}
              onClick={() => {
                setPeriodo('hoje');
                setPagina(1);
              }}
              active={periodo === 'hoje'}
            />
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[180px] flex-1">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setPagina(1);
                }}
                placeholder="Buscar pelo código do registro"
                className="h-8 pl-7 text-xs"
              />
            </div>
            <Select value={status} onValueChange={filtrarPor}>
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as situações</SelectItem>
                <SelectItem value="em_andamento">Em andamento</SelectItem>
                <SelectItem value="concluidas">Concluídas</SelectItem>
                <SelectItem value="com_erro">Com erro</SelectItem>
                <SelectItem value="canceladas">Canceladas</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={periodo}
              onValueChange={(v) => {
                setPeriodo(v);
                setPagina(1);
              }}
            >
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todo o período</SelectItem>
                <SelectItem value="hoje">Hoje</SelectItem>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Tabela rolável */}
          <div className="overflow-auto rounded-lg border">
            <table className="w-full min-w-[720px] text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  {!definitionId && <th className="px-3 py-2 text-left font-medium">Processo</th>}
                  <th className="px-3 py-2 text-left font-medium">Versão</th>
                  <th className="px-3 py-2 text-left font-medium">Registro</th>
                  <th className="px-3 py-2 text-left font-medium">Situação</th>
                  <th className="px-3 py-2 text-left font-medium">Etapa atual</th>
                  <th className="px-3 py-2 text-left font-medium">Início</th>
                  <th className="px-3 py-2 text-left font-medium">Duração</th>
                  {onOpenRecord && <th className="px-3 py-2 text-right font-medium">Registro</th>}
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                      Carregando…
                    </td>
                  </tr>
                )}

                {!isLoading && instances.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                      Nenhuma execução encontrada para estes filtros.
                    </td>
                  </tr>
                )}

                {instances.map((execucao: WorkflowExecutionItem) => (
                  <tr
                    key={execucao.id}
                    className={cn(
                      'cursor-pointer border-t transition-colors hover:bg-muted/40',
                      execucao.status === 'erro' && 'bg-destructive/5',
                    )}
                    onClick={() => setSelecionada(execucao.id)}
                  >
                    {!definitionId && (
                      <td className="max-w-[180px] truncate px-3 py-2">{execucao.definition_name}</td>
                    )}
                    {/* v1.7.0: versão em que a execução rodou */}
                    <td className="px-3 py-2 text-muted-foreground">
                      {execucao.version_number != null ? `v${execucao.version_number}` : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono">
                      {execucao.root_entity_id ? formatUuidShort(execucao.root_entity_id) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <StatusDot status={execucao.status} />
                    </td>
                    <td className="max-w-[200px] truncate px-3 py-2 text-muted-foreground">
                      {execucao.error_summary || execucao.current_node_label || '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{dataHora(execucao.started_at)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {duracao(execucao.duracao_segundos)}
                    </td>
                    {/* v1.2.0: atalho para a ficha do registro de origem */}
                    {onOpenRecord && (
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          disabled={!execucao.root_entity_id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenRecord(execucao.root_entity_type, execucao.root_entity_id);
                          }}
                        >
                          Abrir registro
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  // v1.2.0: modo página (submenu Execuções)
  if (asPage) {
    return (
      <div className="space-y-4">
        {conteudo}
        <div className="border-t pt-3">{rodape}</div>
      </div>
    );
  }

  return (
    <WorkflowDialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={Activity}
      title="Execuções do processo"
      description={
        definitionName
          ? `Acompanhe tudo que já rodou em "${definitionName}".`
          : 'Acompanhe tudo que já rodou nos seus processos.'
      }
      maxWidthClass="sm:max-w-6xl"
      footer={rodape}
    >
      {conteudo}
    </WorkflowDialogShell>
  );
}
